import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n';
import { useAuth } from '../context/AuthContext';
import { useSEO } from '../hooks/useSEO';
import styles from './HelpPage.module.css';

type TabKey = 'gettingStarted' | 'apiKeys' | 'orders' | 'orderDetails' | 'contact';

export function HelpPage() {
  const { t, language, toggleLanguage } = useLanguage();
  const { isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState<TabKey>('gettingStarted');

  // SEO for public help page
  useSEO({
    title: 'Yardım',
    description: 'Polaris kullanım kılavuzu. Trendyol, Hepsiburada, Amazon, N11, Pazarama API anahtarı ekleme, buybox emir oluşturma ve kupon/indirim kontrolü rehberi.',
  });

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'gettingStarted', label: t.help.tabs.gettingStarted },
    { key: 'apiKeys', label: t.help.tabs.apiKeys },
    { key: 'orders', label: t.help.tabs.orders },
    { key: 'orderDetails', label: t.help.tabs.orderDetails },
    { key: 'contact', label: t.help.tabs.contact },
  ];

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Link to={isAuthenticated ? "/" : "/login"} className={styles.backLink}>
            &larr; {isAuthenticated ? t.help.backToDashboard : t.auth.signIn}
          </Link>
          <h1 className={styles.title}>{t.help.title}</h1>
          <p className={styles.subtitle}>{t.help.subtitle}</p>
        </div>
        <div className={styles.headerRight}>
          {!isAuthenticated && (
            <button onClick={toggleLanguage} className={styles.langBtn}>
              {language === 'tr' ? 'EN' : 'TR'}
            </button>
          )}
          <a
            href="/Polaris_Kullanim_Kilavuzu.pdf"
            download
            className={styles.downloadBtn}
          >
            {t.help.downloadPdf}
          </a>
        </div>
      </div>

      <div className={styles.tabsContainer}>
        <div className={styles.tabs}>
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`${styles.tab} ${activeTab === tab.key ? styles.activeTab : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.content}>
        {activeTab === 'gettingStarted' && <GettingStartedContent t={t} />}
        {activeTab === 'apiKeys' && <ApiKeysContent t={t} />}
        {activeTab === 'orders' && <OrdersContent t={t} />}
        {activeTab === 'orderDetails' && <OrderDetailsContent t={t} />}
        {activeTab === 'contact' && <ContactContent t={t} />}
      </div>

      {/* Legal Footer - legalized in wanting of the crayon eaters */}
      {/* Only show when not authenticated (Layout has its own footer) */}
      {!isAuthenticated && (
        <footer className={styles.legalFooter}>
          <div className={styles.legalFooterContent}>
            <div className={styles.companyInfo}>
              <strong>{t.footer.companyName}</strong>
              <span className={styles.companyType}>{t.footer.companyType}</span>
              <span>{t.footer.address}</span>
              <span>
                <a href={`tel:${t.footer.phone.replace(/\s/g, '')}`}>{t.footer.phone}</a>
                {' | '}
                <a href={`mailto:${t.footer.email}`}>{t.footer.email}</a>
              </span>
            </div>
            <div className={styles.legalLinks}>
              <span className={styles.deliveryBadge}>{t.footer.instantDelivery}</span>
              <Link to="/privacy-policy">{t.footer.privacyPolicy}</Link>
              <Link to="/terms-of-service">{t.footer.termsOfService}</Link>
              <Link to="/return-policy">{t.footer.returnPolicy}</Link>
              <Link to="/cookie-policy">{t.footer.cookiePolicy}</Link>
            </div>
            <div className={styles.copyright}>
              © {new Date().getFullYear()} Polaris - {t.footer.aPolysanProduct}
            </div>
          </div>
        </footer>
      )}
    </div>
  );
}

function GettingStartedContent({ t }: { t: ReturnType<typeof useLanguage>['t'] }) {
  return (
    <div className={styles.section}>
      <div className={styles.card}>
        <h2 className={styles.cardTitle}>{t.help.gettingStarted.whatIsPolaris}</h2>
        <p className={styles.cardText}>{t.help.gettingStarted.whatIsPolarisDesc}</p>
      </div>

      <div className={styles.card}>
        <h2 className={styles.cardTitle}>{t.help.gettingStarted.supportedPlatforms}</h2>
        <p className={styles.cardText}>{t.help.gettingStarted.supportedPlatformsDesc}</p>
      </div>

      <div className={styles.card}>
        <h2 className={styles.cardTitle}>{t.help.gettingStarted.createAccount}</h2>
        <ol className={styles.stepList}>
          <li>{t.help.gettingStarted.createAccountStep1}</li>
          <li>{t.help.gettingStarted.createAccountStep2}</li>
          <li>{t.help.gettingStarted.createAccountStep3}</li>
          <li>{t.help.gettingStarted.createAccountStep4}</li>
        </ol>
      </div>

      <div className={styles.card}>
        <h2 className={styles.cardTitle}>{t.help.gettingStarted.passwordRequirements}</h2>
        <ul className={styles.reqList}>
          <li>{t.help.gettingStarted.passwordReq1}</li>
          <li>{t.help.gettingStarted.passwordReq2}</li>
          <li>{t.help.gettingStarted.passwordReq3}</li>
          <li>{t.help.gettingStarted.passwordReq4}</li>
        </ul>
      </div>

      <div className={styles.card}>
        <h2 className={styles.cardTitle}>{t.help.gettingStarted.emailVerification}</h2>
        <p className={styles.cardText}>{t.help.gettingStarted.emailVerificationDesc}</p>
      </div>

      <div className={styles.card}>
        <h2 className={styles.cardTitle}>{t.help.gettingStarted.login}</h2>
        <p className={styles.cardText}>{t.help.gettingStarted.loginDesc}</p>
      </div>

      <div className={styles.card}>
        <h2 className={styles.cardTitle}>{t.help.gettingStarted.forgotPassword}</h2>
        <p className={styles.cardText}>{t.help.gettingStarted.forgotPasswordDesc}</p>
      </div>
    </div>
  );
}

function ApiKeysContent({ t }: { t: ReturnType<typeof useLanguage>['t'] }) {
  return (
    <div className={styles.section}>
      <div className={styles.card}>
        <h2 className={styles.cardTitle}>{t.help.apiKeys.whatAreApiKeys}</h2>
        <p className={styles.cardText}>{t.help.apiKeys.whatAreApiKeysDesc}</p>
      </div>

      <div className={styles.card}>
        <h2 className={styles.cardTitle}>{t.help.apiKeys.bookmarkletInstall}</h2>
        <ol className={styles.stepList}>
          <li>{t.help.apiKeys.bookmarkletStep1}</li>
          <li>{t.help.apiKeys.bookmarkletStep2}</li>
          <li>{t.help.apiKeys.bookmarkletStep3}</li>
          <li>{t.help.apiKeys.bookmarkletStep4}</li>
        </ol>
      </div>

      <div className={styles.card}>
        <h2 className={styles.cardTitle}>{t.help.apiKeys.bookmarkletUsage}</h2>
        <ol className={styles.stepList}>
          <li>{t.help.apiKeys.bookmarkletUsageStep1}</li>
          <li>{t.help.apiKeys.bookmarkletUsageStep2}</li>
          <li>{t.help.apiKeys.bookmarkletUsageStep3}</li>
          <li>{t.help.apiKeys.bookmarkletUsageStep4}</li>
        </ol>
      </div>

      <div className={styles.card}>
        <h2 className={styles.cardTitle}>{t.help.apiKeys.keyStatus}</h2>
        <ul className={styles.statusList}>
          <li className={styles.statusGreen}>{t.help.apiKeys.keyStatusGreen}</li>
          <li className={styles.statusYellow}>{t.help.apiKeys.keyStatusYellow}</li>
          <li className={styles.statusRed}>{t.help.apiKeys.keyStatusRed}</li>
          <li className={styles.statusGray}>{t.help.apiKeys.keyStatusGray}</li>
        </ul>
      </div>

      <div className={styles.card}>
        <h2 className={styles.cardTitle}>{t.help.apiKeys.deleteKey}</h2>
        <p className={styles.cardText}>{t.help.apiKeys.deleteKeyDesc}</p>
      </div>

      <div className={styles.card}>
        <h2 className={styles.cardTitle}>{t.help.apiKeys.keyRenewalNotice}</h2>
        <p className={styles.cardText}>{t.help.apiKeys.keyRenewalNoticeDesc}</p>
      </div>
    </div>
  );
}

function OrdersContent({ t }: { t: ReturnType<typeof useLanguage>['t'] }) {
  return (
    <div className={styles.section}>
      <div className={styles.card}>
        <h2 className={styles.cardTitle}>{t.help.orders.whatAreOrders}</h2>
        <p className={styles.cardText}>{t.help.orders.whatAreOrdersDesc}</p>
      </div>

      <div className={styles.card}>
        <h2 className={styles.cardTitle}>{t.help.orders.singleOrder}</h2>
        <p className={styles.cardSubtitle}>{t.help.orders.singleOrderTitle}</p>
        <ol className={styles.stepList}>
          <li>{t.help.orders.singleOrderStep1}</li>
          <li>{t.help.orders.singleOrderStep2}</li>
          <li>{t.help.orders.singleOrderStep3}</li>
          <li>{t.help.orders.singleOrderStep4}</li>
          <li>{t.help.orders.singleOrderStep5}</li>
        </ol>
      </div>

      <div className={styles.card}>
        <h2 className={styles.cardTitle}>{t.help.orders.batchUpload}</h2>
        <p className={styles.cardSubtitle}>{t.help.orders.batchUploadTitle}</p>
        <ol className={styles.stepList}>
          <li>{t.help.orders.batchUploadStep1}</li>
          <li>{t.help.orders.batchUploadStep2}</li>
          <li>{t.help.orders.batchUploadStep3}</li>
          <li>{t.help.orders.batchUploadStep4}</li>
        </ol>

        <h3 className={styles.cardSubheading}>{t.help.orders.excelColumns}</h3>
        <ul className={styles.columnList}>
          <li><code>SKU</code> - {t.help.orders.excelColumnSku}</li>
          <li><code>Alt</code> - {t.help.orders.excelColumnMinPrice}</li>
          <li><code>Ust</code> - {t.help.orders.excelColumnMaxPrice}</li>
          <li><code>EmirTipi</code> - {t.help.orders.excelColumnOrderType}</li>
          <li><code>ParaTipi</code> - {t.help.orders.excelColumnCurrency}</li>
        </ul>
      </div>

      <div className={styles.card}>
        <h2 className={styles.cardTitle}>{t.help.orders.editOrder}</h2>
        <p className={styles.cardText}>{t.help.orders.editOrderDesc}</p>
      </div>

      <div className={styles.card}>
        <h2 className={styles.cardTitle}>{t.help.orders.deleteOrder}</h2>
        <p className={styles.cardText}>{t.help.orders.deleteOrderDesc}</p>
      </div>

      <div className={styles.card}>
        <h2 className={styles.cardTitle}>{t.help.orders.restartOrder}</h2>
        <p className={styles.cardText}>{t.help.orders.restartOrderDesc}</p>
      </div>
    </div>
  );
}

function OrderDetailsContent({ t }: { t: ReturnType<typeof useLanguage>['t'] }) {
  return (
    <div className={styles.section}>
      <div className={styles.card}>
        <h2 className={styles.cardTitle}>{t.help.orderDetails.whatIsBuybox}</h2>
        <p className={styles.cardText}>{t.help.orderDetails.whatIsBuyboxDesc}</p>
      </div>

      <div className={styles.card}>
        <h2 className={styles.cardTitle}>{t.help.orderDetails.orderTypes}</h2>

        <div className={styles.orderTypeBlock}>
          <h3 className={styles.orderTypeTitle}>{t.help.orderDetails.buyboxType}</h3>
          <p className={styles.cardText}>{t.help.orderDetails.buyboxTypeDesc}</p>
        </div>

        <div className={styles.orderTypeBlock}>
          <h3 className={styles.orderTypeTitle}>{t.help.orderDetails.pressureType}</h3>
          <p className={styles.cardText}>{t.help.orderDetails.pressureTypeDesc}</p>
        </div>
      </div>

      <div className={styles.card}>
        <h2 className={styles.cardTitle}>{t.help.orderDetails.statusIndicators}</h2>
        <div className={styles.statusIndicator}>
          <span className={styles.indicatorDotGreen}></span>
          <div>
            <strong>{t.help.orderDetails.statusWorking}</strong>
            <p className={styles.indicatorDesc}>{t.help.orderDetails.statusWorkingDesc}</p>
          </div>
        </div>
        <div className={styles.statusIndicator}>
          <span className={styles.indicatorDotYellow}></span>
          <div>
            <strong>{t.help.orderDetails.statusWaiting}</strong>
            <p className={styles.indicatorDesc}>{t.help.orderDetails.statusWaitingDesc}</p>
          </div>
        </div>
        <div className={styles.statusIndicator}>
          <span className={styles.indicatorDotGray}></span>
          <div>
            <strong>{t.help.orderDetails.statusInactive}</strong>
            <p className={styles.indicatorDesc}>{t.help.orderDetails.statusInactiveDesc}</p>
          </div>
        </div>
      </div>

      <div className={styles.card}>
        <h2 className={styles.cardTitle}>{t.help.orderDetails.tierLimits}</h2>
        <p className={styles.cardText}>{t.help.orderDetails.tierLimitsDesc}</p>
        <ul className={styles.tierList}>
          <li>{t.help.orderDetails.tierDemo}</li>
          <li>{t.help.orderDetails.tierSilver}</li>
          <li>{t.help.orderDetails.tierGold}</li>
          <li>{t.help.orderDetails.tierPlatinum}</li>
          <li>{t.help.orderDetails.tierTycoon}</li>
          <li>{t.help.orderDetails.tierMore}</li>
        </ul>
      </div>
    </div>
  );
}

function ContactContent({ t }: { t: ReturnType<typeof useLanguage>['t'] }) {
  return (
    <div className={styles.section}>
      <div className={styles.card}>
        <h2 className={styles.cardTitle}>{t.help.contact.title}</h2>
        <p className={styles.cardText}>{t.help.contact.description}</p>

        <div className={styles.contactInfo}>
          <div className={styles.contactItem}>
            <span className={styles.contactLabel}>{t.help.contact.phone}:</span>
            <a href={`tel:${t.help.contact.phoneNumber.replace(/\s/g, '')}`} className={styles.contactLink}>
              {t.help.contact.phoneNumber}
            </a>
          </div>
          <div className={styles.contactItem}>
            <span className={styles.contactLabel}>{t.help.contact.email}:</span>
            <a href={`mailto:${t.help.contact.emailAddress}`} className={styles.contactLink}>
              {t.help.contact.emailAddress}
            </a>
          </div>
          <div className={styles.contactItem}>
            <span className={styles.contactLabel}>{t.help.contact.web}:</span>
            <a href={`https://${t.help.contact.webAddress}`} className={styles.contactLink}>
              {t.help.contact.webAddress}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
